// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module AnalyticsDashboardPage
 * @package @geenius/analytics/react-css
 * @description Composes the primary React analytics dashboard page for the
 * vanilla-CSS variant. The page exposes a ready-to-render overview surface for
 * traffic and engagement reporting without relying on Tailwind utilities.
 */

import {
  type AnalyticsDashboardData,
  type AnalyticsEvent,
  type CohortAnalysis,
  type FunnelResult,
  type RealtimeStats,
  buildAnalyticsSeoMeta,
} from "@geenius/analytics";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@geenius/ui/react-css";
import type React from "react";
import {
  DashboardBuilder,
  type DashboardBuilderTimeRangeOption,
  type ReactCssDashboardLayout,
  type ReactCssDashboardWidget,
} from "../components/DashboardBuilder";
import { AnalyticsPageErrorBoundary } from "../components/AnalyticsPageErrorBoundary";
import { DeviceBreakdown } from "../components/DeviceBreakdown";
import {
  type AnalyticsErrorStateValue,
  ErrorState,
} from "../components/ErrorState";
import { EventTable } from "../components/EventTable";
import { StatsCard } from "../components/StatsCard";
import { TopPagesTable } from "../components/TopPagesTable";
import { useAnalyticsCopy } from "../lib/analyticsCopy";
import { cx } from "../lib/cx";
import { FadeIn } from "../lib/FadeIn";

export type AnalyticsSeoMeta = ReturnType<typeof buildAnalyticsSeoMeta>;
type AnalyticsDashboardBuilderLayout =
  | ReactCssDashboardLayout
  | ReactCssDashboardWidget[];

/**
 * Props for rendering the React CSS analytics dashboard page.
 *
 * @property data Optional aggregate dashboard data rendered across the page sections.
 * @property recentEvents Optional list of recent analytics events shown in the event table.
 * @property realtimeStats Optional realtime activity snapshot rendered in the builder section.
 * @property funnelResult Optional funnel result rendered in the builder section.
 * @property cohortAnalysis Optional cohort analysis rendered in the builder section.
 * @property builderLayout Optional explicit builder layout for custom dashboard widgets.
 * @property error Optional page-level error surfaced before dashboard data sections.
 * @property onRetry Optional recovery callback shown in the page-level error state.
 * @property isLoading Whether child widgets should render loading placeholders instead of content.
 * @property renderSeoMeta Optional renderer for host apps that mount SEO metadata.
 * @property className Optional class name merged into the page wrapper.
 */
export interface AnalyticsDashboardPageProps {
  data?: AnalyticsDashboardData | null;
  recentEvents?: AnalyticsEvent[];
  realtimeStats?: RealtimeStats | null;
  funnelResult?: FunnelResult | null;
  cohortAnalysis?: CohortAnalysis | null;
  builderLayout?: AnalyticsDashboardBuilderLayout | null;
  builderEditable?: boolean;
  builderTimeRange?: string;
  builderTimeRangeOptions?: DashboardBuilderTimeRangeOption[];
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  onBuilderLayoutChange?: (widgets: ReactCssDashboardWidget[]) => void;
  onBuilderTimeRangeChange?: (timeRange: string) => void;
  isLoading?: boolean;
  renderSeoMeta?: (meta: AnalyticsSeoMeta) => React.ReactNode;
  className?: string;
}

function buildSupplementalLayout({
  cohortAnalysis,
  copy,
  funnelResult,
  isLoading,
  realtimeStats,
}: {
  cohortAnalysis?: CohortAnalysis | null;
  copy: ReturnType<typeof useAnalyticsCopy>;
  funnelResult?: FunnelResult | null;
  isLoading?: boolean;
  realtimeStats?: RealtimeStats | null;
}): ReactCssDashboardLayout | null {
  const widgets: ReactCssDashboardWidget[] = [];

  if (realtimeStats !== undefined || isLoading) {
    widgets.push({
      id: "dashboard-realtime",
      kind: "realtime",
      title: copy.t("analytics.dashboard.realtime"),
      description: copy.t("analytics.dashboard.realtimeDescription"),
      isLoading,
      stats: realtimeStats ?? null,
      span: 1,
    });
  }

  if (funnelResult) {
    widgets.push({
      id: "dashboard-funnel",
      kind: "funnel",
      title: funnelResult.name,
      result: funnelResult,
      span: 2,
    });
  }

  if (cohortAnalysis) {
    widgets.push({
      id: "dashboard-cohorts",
      kind: "cohort",
      title: copy.t("analytics.dashboard.cohorts"),
      description: copy.t("analytics.dashboard.cohortsDescription"),
      analysis: cohortAnalysis,
      span: 3,
    });
  }

  if (widgets.length === 0) return null;

  return {
    title: "Configurable dashboard",
    description:
      "Live, funnel, and cohort widgets can be reordered or edited by host apps.",
    widgets,
  };
}

/**
 * Renders the React CSS analytics dashboard page with summary cards and detail tables.
 *
 * @param props Page props containing dashboard aggregates, recent events, and loading state.
 * @returns A full-page React analytics dashboard composition.
 */
export function AnalyticsDashboardPage({
  builderEditable,
  builderLayout,
  builderTimeRange,
  builderTimeRangeOptions,
  cohortAnalysis,
  data,
  error,
  funnelResult,
  recentEvents = [],
  realtimeStats,
  isLoading,
  onRetry,
  onBuilderLayoutChange,
  onBuilderTimeRangeChange,
  renderSeoMeta,
  className,
}: AnalyticsDashboardPageProps): React.ReactElement {
  const copy = useAnalyticsCopy();
  const seoMeta = buildAnalyticsSeoMeta({
    title: copy.t("analytics.seo.dashboardTitle"),
    description: copy.t("analytics.seo.dashboardDescription"),
    path: "/analytics",
    keywords: ["analytics", "traffic", "engagement", "funnels", "events"],
  });
  const supplementalLayout = buildSupplementalLayout({
    cohortAnalysis,
    copy,
    funnelResult,
    isLoading,
    realtimeStats,
  });
  const resolvedBuilderLayout = builderLayout ?? supplementalLayout;
  const pageHeader = (
    <FadeIn>
      <header className="gn-analytics-page__header" data-analytics-page-header>
        <h1 className="gn-analytics-page__title">
          {copy.t("analytics.dashboard.title")}
        </h1>
        <p className="gn-analytics-page__subtitle">
          {copy.t("analytics.dashboard.subtitle")}
        </p>
      </header>
    </FadeIn>
  );

  return (
    <>
      {renderSeoMeta ? renderSeoMeta(seoMeta) : null}
      <AnalyticsPageErrorBoundary onRetry={onRetry}>
        <div
          className={cx("gn-analytics-page", className)}
          data-analytics-page="dashboard"
        >
          {pageHeader}

          {error ? (
            <FadeIn delay={60}>
              <Card data-analytics-page-error>
                <CardContent>
                  <ErrorState error={error} onRetry={onRetry} />
                </CardContent>
              </Card>
            </FadeIn>
          ) : (
            <>
              <FadeIn
                delay={60}
                className="gn-analytics-dashboard__kpi-grid"
                data-analytics-stats-row
              >
                <StatsCard
                  label={copy.t("analytics.dashboard.pageViews")}
                  value={data?.totalPageViews.toLocaleString() ?? "—"}
                  icon="👁️"
                />
                <StatsCard
                  label={copy.t("analytics.dashboard.uniqueVisitors")}
                  value={data?.uniqueVisitors.toLocaleString() ?? "—"}
                  icon="👤"
                />
                <StatsCard
                  label={copy.t("analytics.dashboard.topReferrers")}
                  value={data?.topReferrers?.length?.toString() ?? "0"}
                  icon="🔗"
                />
                <StatsCard
                  label={copy.t("analytics.dashboard.devices")}
                  value={Object.keys(
                    data?.browserBreakdown ?? {},
                  ).length.toString()}
                  icon="📊"
                />
              </FadeIn>

              <FadeIn delay={120}>
                <h2 className="gn-analytics-a11y__sr-only">
                  {copy.t("analytics.dashboard.overview")}
                </h2>
                <Card data-analytics-overview-section>
                  <CardHeader>
                    <CardTitle data-analytics-section-title>
                      {copy.t("analytics.dashboard.overview")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div
                      className="gn-analytics-dashboard__chart-grid"
                      data-analytics-grid
                    >
                      <TopPagesTable
                        pages={data?.topPages ?? []}
                        isLoading={isLoading}
                      />
                      <DeviceBreakdown
                        breakdown={
                          data?.deviceBreakdown ?? {
                            desktop: 0,
                            tablet: 0,
                            mobile: 0,
                          }
                        }
                        isLoading={isLoading}
                      />
                    </div>
                  </CardContent>
                </Card>
              </FadeIn>

              {resolvedBuilderLayout ? (
                <FadeIn delay={180}>
                  <h2 className="gn-analytics-a11y__sr-only">
                    Configurable analytics dashboard
                  </h2>
                  <DashboardBuilder
                    editable={builderEditable}
                    layout={resolvedBuilderLayout}
                    onLayoutChange={onBuilderLayoutChange}
                    onTimeRangeChange={onBuilderTimeRangeChange}
                    timeRange={builderTimeRange}
                    timeRangeOptions={builderTimeRangeOptions}
                  />
                </FadeIn>
              ) : null}

              <FadeIn delay={240}>
                <h2 className="gn-analytics-a11y__sr-only">
                  {copy.t("analytics.dashboard.recentEvents")}
                </h2>
                <Card data-analytics-section>
                  <CardHeader>
                    <CardTitle data-analytics-section-title>
                      {copy.t("analytics.dashboard.recentEvents")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <EventTable
                      events={recentEvents}
                      isLoading={isLoading}
                      maxRows={15}
                    />
                  </CardContent>
                </Card>
              </FadeIn>
            </>
          )}
        </div>
      </AnalyticsPageErrorBoundary>
    </>
  );
}
